import React from 'react';
import { GexProfileWidget } from '../GexProfileWidget';
import { GammaTables } from './GammaTables';
import type { GexWidgetSettings } from '../../types/gex';

interface Props { symbol: string; }

/**
 * The Gamma tab — two Gamma Exposure panels, configured here and presented as
 * fixed readouts rather than tools to adjust in place.
 *
 * These are the dashboard widget itself, not a reimplementation, so whatever
 * is fixed or added there lands here too.
 *
 * Each carries its own `windowId`, and the widget persists settings to the
 * backend keyed on that id. A preset therefore only applies to a pane with no
 * saved state — editing one here does NOT reach a pane that has already been
 * opened. Bump the id suffix to force it through, accepting that the pane
 * forgets everything else it was set to.
 */

// Tall on purpose: only two panels on the page, and a gamma profile is a
// vertical price ladder, so height is what makes the strikes legible. Revisit
// if a key-stats block ever lands above them.
const WIDGET_HEIGHT = 780;

const PANELS: Array<{
  windowId: string;
  title: string;
  defaults: Partial<GexWidgetSettings>;
  /** Re-snap to the soonest expiry on every load. See the widget's prop. */
  pinNearest?: boolean;
}> = [
  {
    // Suffix bumped to -a3 so the new presets reach panes that already have
    // saved state from the previous configuration.
    windowId: 'ticker-explorer-gamma-a3',
    // The ticker is already in the page header and the hero bar; a third
    // mention would crowd out the one thing this header can usefully say,
    // which is what the panel measures.
    title: 'Net Gamma — Next Expiry',
    defaults: {
      showCandlesticks: false,
      metric: 'gex',
      displayMode: 'net',
      byExpiration: true,
      // Left null: the widget snaps it to the nearest real expiry once the
      // contract list loads. That is what "next expiry" has to mean for a
      // symbol whose cycle we cannot assume.
      expiryDate: null,
    },
    // ⚠️ Required, not decorative. Saved settings survive per windowId, and a
    // date saved yesterday is still a valid expiry today — so without this the
    // panel silently sits on tomorrow's chain. SPY showed 08/05 on the 4th
    // while its 0DTE chain was listed and ignored.
    pinNearest: true,
  },
  {
    windowId: 'ticker-explorer-gamma-b3',
    title: 'Net Gamma — All Expiries',
    defaults: {
      showCandlesticks: false,
      metric: 'gex',
      displayMode: 'net',
      byExpiration: false,
      expiryDate: null,
    },
  },
];

export function GammaSection({ symbol }: Props): React.ReactElement {
  return (
    <div className="p-3 space-y-3">
      {/* The widget brings its own rounded surface but no border, so it reads
          as a card on the dashboard where it sits alone. Here it needs the
          hairline to match the sections around it. */}
      <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3">
        {PANELS.map(({ windowId, title, defaults, pinNearest }) => (
          <div
            key={windowId}
            className="rounded-lg border border-dark-border overflow-hidden"
            style={{ height: WIDGET_HEIGHT }}
          >
            <GexProfileWidget
              windowId={windowId}
              title={title}
              // Pinned to the page ticker, and no toolbar at all — these are
              // configured above, not adjusted in place.
              lockedSymbol={symbol}
              hideControls
              // Fixed readout: the page scrolls, so the chart must not eat
              // the wheel, and there is nothing here to explore into.
              disableZoom
              defaultSettings={defaults}
              pinNearestExpiry={pinNearest}
              // Not removable here — fixtures of the page, not dashboard tiles
              // the user placed.
              onRemove={() => {}}
            />
          </div>
        ))}
      </div>

      {/* The charts show the shape; these give the numbers behind it. */}
      <GammaTables symbol={symbol} />
    </div>
  );
}
